import { DOCUMENT } from '@angular/common';
import { Injectable, computed, effect, inject, signal } from '@angular/core';

export type ColorSchemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'sm-color-scheme';
const DARK_CLASS = 'sm-dark';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * Reads the stored preference, defaulting to 'system' (follow the OS /
 * browser). Safe to call before Angular is running.
 */
export function readColorSchemePreference(): ColorSchemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(win: Window | null): boolean {
  return win?.matchMedia?.(DARK_QUERY).matches ?? false;
}

function applyScheme(doc: Document, dark: boolean): void {
  const root = doc.documentElement;
  root.classList.toggle(DARK_CLASS, dark);
  // So scrollbars and native inputs follow the look too.
  root.style.colorScheme = dark ? 'dark' : 'light';
}

/**
 * Sets the dark class from the stored preference without Angular. Call it in
 * main.ts before `bootstrapModule`, so the page doesn't flash white first;
 * `ColorSchemeService` takes over once the app runs.
 */
export function applyInitialColorScheme(doc: Document = document): void {
  const preference = readColorSchemePreference();
  const dark =
    preference === 'dark' ||
    (preference === 'system' && systemPrefersDark(doc.defaultView));
  applyScheme(doc, dark);
}

/**
 * Light / dark look of the app: 'system' follows `prefers-color-scheme`
 * (live), 'light' and 'dark' override it. The choice is remembered in
 * localStorage. Separate from ThemeModeService, which is legacy vs next.
 */
@Injectable({ providedIn: 'root' })
export class ColorSchemeService {
  private readonly _document = inject(DOCUMENT);
  private readonly _preference = signal<ColorSchemePreference>(
    readColorSchemePreference()
  );
  private readonly _systemDark = signal(
    systemPrefersDark(this._document.defaultView)
  );

  readonly preference = this._preference.asReadonly();
  readonly isDark = computed(() => {
    const preference = this._preference();
    return preference === 'system' ? this._systemDark() : preference === 'dark';
  });

  constructor() {
    const query = this._document.defaultView?.matchMedia?.(DARK_QUERY);
    query?.addEventListener?.('change', (event) =>
      this._systemDark.set(event.matches)
    );
    effect(() => applyScheme(this._document, this.isDark()));
  }

  setPreference(preference: ColorSchemePreference): void {
    this._preference.set(preference);
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Storage unavailable: the choice just lasts for this page.
    }
  }
}
